import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { t } from "i18next";
import { callForHelpService } from "../services/call-for-help.services";

class CallForHelpStore {
  surveys = [];
  surveyOptions = [];
  selectedSurvey = null; // ✅ holds selected survey object
  selectedQuestions = [];
  answers = {}; // ✅ { [questionId]: value }
  submitting = false;
  loading = false;
  initializing = true; // ✅ added
  error = null;
  feedbackType = "";
  site = "allsites";
  emailAddress = "";
  isUploading = false;
  evidenceFiles = [];
  attachmentResetKey = 0; // ✅ increment triggers panel reset

  resetAttachments = () => {
    runInAction(() => {
      this.evidenceFiles = [];
      this.attachmentResetKey += 1; // ✅ signals panel to clear local state
    });
  };

  setSite = (val) => {
    this.site = val;
  };
  setEmailAddress = (val) => {
    this.emailAddress = val;
  };

  setFeedbackType = (val) => {
    this.feedbackType = val;
    this.selectedSurvey = this.surveys.find((s) => s._id === val) || null;
    this.selectedQuestions = this.selectedSurvey?.questions ?? []; // ✅ null check
    this.answers = {};
  };

  setAnswer(questionId, value) {
    // ✅ spread array to plain array — avoid MobX observable array issues
    this.answers = {
      ...this.answers,
      [questionId]: Array.isArray(value) ? [...value] : value,
    };
  }
  constructor() {
    makeAutoObservable(this);
  }
  // ─── INITIALIZE ───────────────────────────────────────────────────────
  initialize = async () => {
    runInAction(() => {
      this.initializing = true;
    });
    await this.fetchSurveys();
    runInAction(() => {
      this.initializing = false;
    });
  };
  fetchSurveys = async () => {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const response = await callForHelpService.getSurveys();
      const data = response?.data || response;
      const surveys = data?.data ?? data?.result ?? [];

      runInAction(() => {
        this.surveys = surveys;
        // ✅ Map surveys to options
        this.surveyOptions = surveys.map((survey) => ({
          value: survey._id,
          label: survey.name,
        }));

        // ✅ Auto-select first survey
        if (surveys.length > 0) {
          this.feedbackType = surveys[0]._id;
          this.selectedSurvey = surveys[0];
          this.selectedQuestions = surveys[0].questions;
        }
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        t("Failed to fetch surveys");
      showToast.error(message);
      runInAction(() => {
        this.surveys = [];
        this.surveyOptions = [];
        this.selectedQuestions = [];
        this.error = message;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
        this.initializing = false;
      });
    }
  };

  handleSubmit = async (payload) => {
    runInAction(() => {
      this.submitting = true;
      this.error = null;
    });
    try {
      const response = await callForHelpService.submitCallForHelp(payload);
      const data = response?.data || response;
      const result = data?.result || data;

      return { success: !!result, data: result };
    } catch (err) {
      const message =
        err?.response?.data?.message ?? err?.message ?? t("Submit failed");
      runInAction(() => {
        this.error = message;
      });
      showToast.error(message);
      return { success: false, data: null };
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  };

  uploadAttachment = async (companyId, files) => {
    runInAction(() => {
      this.isUploading = true;
    });
    if (!companyId) return false;

    try {
      const uploadPromise = callForHelpService.uploadAttachment(
        companyId,
        files,
      );

      showToast.promise(uploadPromise, {
        loading: "Uploading files...",
        success: "Files uploaded",
        error: "Failed to upload files",
      });
      const response = await uploadPromise;
      const result = response?.data ?? response;

      runInAction(() => {
        this.evidenceFiles = result.map((f) => ({
          fileId: f._id,
          name: f.originalName,
          size: f.size,
          type: f.mimeType,
          url: f.url,
          uploadedAt: f.createdAt,
          companyId: f.companyId,
          userId: f.userId,
        }));
      });

      return true;
    } catch (err) {
      showToast.error("Failed to upload files");
      return false;
    } finally {
      runInAction(() => {
        this.isUploading = false;
      });
    }
  };
  downloadAttachment = async (proof) => {
    const fileId = proof.fileId;
    if (!fileId) return;
    const downloadPromise = callForHelpService.downloadAttachment(proof);
    showToast.promise(downloadPromise, {
      loading: "Downloading",
      success: "Successfully downloaded",
      error: "Failed to download",
    });
    await promiseWithMinDelay(downloadPromise);
  };

  deleteAttachment = async (fileId) => {
    try {
      await callForHelpService.deleteAttachment(fileId);
      this.removeEvidenceFile(fileId);
      showToast.success("Successfully deleted!");
    } catch (error) {
      showToast.error(error?.message || "Failed to delete!");
      throw error;
    }
  };
  removeEvidenceFile = (fileId) => {
    this.evidenceFiles = this.evidenceFiles.filter((f) => f.fileId !== fileId);
  };
}

export const callForHelpStore = new CallForHelpStore();
